import React, { useState, useEffect } from "react";
import { Panel, Loader, Table } from "rsuite";
import axios from "../../../utilities/axios";
import { ChannelSelectData } from "../../../core/constants/Channels";
import { MethodsUtils } from "../../../core/utils/MethodsUtils";

const { Column, HeaderCell, Cell } = Table

type Props = {
    channels: string[];
    periods: any;
    searchTrigger: number;
}

interface SortData {
    sortColumn?: string;
    sortType?: 'asc' | 'desc';
}

const FreePeriodComparisonPanel: React.FC<Props> = ({ channels, periods, searchTrigger }) => {
    const [rows, setRows] = useState<any[]>([])
    const [loading, setLoading] = useState(false)
    const [sortData, setSortData] = useState<SortData>({})

    const updateTable = () => {
        setLoading(true)
        setTimeout(() => setLoading(false), 100)
    }

    const fetchData = async () => {
        setLoading(true)
        try {
            const { data } = await axios.post('/api/v1/analytics/sales/free_period_comparison', { channels, periods })
            setRows(Array.isArray(data) ? data : [])
        } catch (err) {
            console.error(err)
            setRows([])
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (searchTrigger > 0) {
            fetchData()
        }
    }, [searchTrigger])

    const handleSortColumn = (sortColumn: string, sortType?: 'asc' | 'desc') => {
        setSortData({ sortColumn, sortType })
        const sorted = [...rows].sort((a, b) => {
            const aVal = a[sortColumn]
            const bVal = b[sortColumn]
            if (typeof aVal === 'string') {
                return sortType === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
            }
            return sortType === 'asc' ? aVal - bVal : bVal - aVal
        })
        setRows(sorted)
    }

    const columnWidth = 150
    return (
        <Panel onEnter={updateTable} bordered header={<strong>Comparação Período Livre</strong>} style={{ background: 'white', borderColor: 'rgb(255, 140, 66)' }} collapsible>

            {loading ? (
                <div style={{ padding: 24, display: 'flex', justifyContent: 'center' }}><Loader content="Carregando..." /></div>
            ) : rows.length === 0 ? (
                <div style={{ padding: 16 }}>Nenhum dado</div>
            ) : (
                <Table key={rows.length} data={rows} sortColumn={sortData.sortColumn} sortType={sortData.sortType} onSortColumn={handleSortColumn} autoHeight hover>
                    <Column width={250} sortable>
                        <HeaderCell>LOJA</HeaderCell>
                        <Cell dataKey={'loja'} />
                    </Column>

                    {ChannelSelectData.map((channel) => {
                        return (
                            <Column key={channel.value + '_periodo_a'} width={columnWidth} sortable>
                                <HeaderCell>{channel.label} (A)</HeaderCell>
                                <Cell dataKey={channel.value + '_periodo_a'}>
                                    {(rowData: any) => MethodsUtils.formatCurrency(rowData[channel.value + '_periodo_a'])}
                                </Cell>
                            </Column>
                        )
                    })}

                    <Column width={columnWidth} sortable>
                        <HeaderCell>TOTAL (A)</HeaderCell>
                        <Cell dataKey={'periodo_a_total'}>
                            {(rowData: any) => MethodsUtils.formatCurrency(rowData.periodo_a_total)}
                        </Cell>
                    </Column>

                    {ChannelSelectData.map((channel) => {
                        return (
                            <Column key={channel.value + '_periodo_b'} width={columnWidth} sortable>
                                <HeaderCell>{channel.label} (B)</HeaderCell>
                                <Cell dataKey={channel.value + '_periodo_b'}>
                                    {(rowData: any) => MethodsUtils.formatCurrency(rowData[channel.value + '_periodo_b'])}
                                </Cell>
                            </Column>
                        )
                    })}

                    <Column width={columnWidth} sortable>
                        <HeaderCell>TOTAL (B)</HeaderCell>
                        <Cell dataKey={'periodo_b_total'}>
                            {(rowData: any) => MethodsUtils.formatCurrency(rowData.periodo_b_total)}
                        </Cell>
                    </Column>

                    <Column width={columnWidth} sortable>
                        <HeaderCell>VAR. R$</HeaderCell>
                        <Cell dataKey={'total_variacao'}>
                            {(rowData: any) => MethodsUtils.formatCurrency(rowData.total_variacao, { signDisplay: "always" })}
                        </Cell>
                    </Column>

                    <Column width={columnWidth} sortable>
                        <HeaderCell>VAR. %</HeaderCell>
                        <Cell dataKey={'total_variacao_percentual'}>
                            {(rowData: any) => MethodsUtils.formatPercentual(rowData.total_variacao_percentual, { signDisplay: "always" })}
                        </Cell>
                    </Column>

                    {ChannelSelectData.map((channel) => {
                        return (
                            <Column key={channel.value + '_variacao'} width={columnWidth} sortable>
                                <HeaderCell>{channel.label} VAR.</HeaderCell>
                                <Cell dataKey={channel.value + '_variacao'}>
                                    {(rowData: any) => MethodsUtils.formatPercentual(rowData[channel.value + '_variacao'], { signDisplay: "always" })}
                                </Cell>
                            </Column>
                        )
                    })}
                </Table>
            )}
        </Panel>
    )
}

export default FreePeriodComparisonPanel
